/**
 * @architecture-header 1
 * @layer app
 * @domain identity-access
 * @responsibility Render the IAM-F12 teacher-only personal and Organization workspace selector.
 * @state-owner ephemeral selector UI plus the auth-owned active context and URL workspace selection
 * @allowed-imports React; React Router; auth module public API; environment and active-role providers; shared marks; shared workspace-identity refresh registry
 * @forbidden local persistence; audience switching; direct API clients; client authorization authority
 * @public-api Agent A workspace composition
 * @side-effects reads fresh context and changes the teacher workspace URL after membership validation; WorkspaceShell commits the URL-selected active context; refetches on a SET-F04 organization rename
 * @tests WorkspaceSwitcher.test.tsx and IAM-F12 browser evidence
 */

import type { MeContextResponse } from '@vispo/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadMeContext } from '../../../modules/auth';
import { VispoAnimatedMark } from '../../../shared/components/BrandMarks';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { registerWorkspaceIdentityRefresh } from '../../../shared/lib/workspace-identity-refresh';
import { useOptionalWorkspaceEnvironment } from '../../config/environment';
import { useActiveOrganizationId, useActiveRole } from '../../providers/ActiveRoleProvider';
import './workspace-switcher.css';

type ContextFixture = {
  status: 'empty' | 'error' | 'loading' | 'ready';
  context?: MeContextResponse;
};

type SelectorStatus = 'denied' | 'error' | 'loading' | 'ready';

function isTeacherOrganization(organization: MeContextResponse['organizations'][number]) {
  return (
    organization.is_classroom_educator ||
    organization.membership_role === 'owner' ||
    organization.membership_role === 'admin' ||
    organization.membership_role === 'staff'
  );
}

function projectTeacherWorkspaces(
  organizations: MeContextResponse['organizations'],
): MeContextResponse['organizations'] {
  const personal = organizations.find((organization) => organization.kind === 'personal');
  const eligibleOrganizations = organizations.filter(
    (organization) => organization.kind !== 'personal' && isTeacherOrganization(organization),
  );
  return personal ? [personal, ...eligibleOrganizations] : eligibleOrganizations;
}

function firstCharacter(name: string): string {
  return Array.from(name.trim())[0] ?? '組';
}

function WorkspaceIdentity({
  organization,
}: {
  organization: MeContextResponse['organizations'][number];
}) {
  if (organization.kind === 'personal') {
    return <VispoAnimatedMark aria-hidden="true" className="iam-f12-workspace-switcher__vispo" />;
  }
  if (organization.logo_url) {
    return (
      <img
        alt=""
        aria-hidden="true"
        className="iam-f12-workspace-switcher__logo"
        src={organization.logo_url}
      />
    );
  }
  return (
    <span aria-hidden="true" className="iam-f12-workspace-switcher__initial">
      {firstCharacter(organization.name)}
    </span>
  );
}

function WorkspaceSwitcher({ surface }: { surface: 'desktop' | 'mobile' }) {
  const environment = useOptionalWorkspaceEnvironment();
  const role = useActiveRole();
  const activeOrganizationId = useActiveOrganizationId();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SelectorStatus>('loading');
  const [organizations, setOrganizations] = useState<MeContextResponse['organizations']>([]);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);

  const readContext = useCallback(async (): Promise<MeContextResponse> => {
    if (!environment) throw new Error('workspace environment is unavailable');
    const fixture = environment.testAdapter?.read('iamF12Context') as ContextFixture | undefined;
    if (fixture?.status === 'loading') return new Promise<MeContextResponse>(() => undefined);
    if (fixture?.status === 'error') throw new Error('IAM-F12 context fixture failed');
    if (fixture?.status === 'empty') {
      return { profile: null, preferences: null, organizations: [] };
    }
    if (fixture?.status === 'ready' && fixture.context) return fixture.context;
    if (environment.demoMode) {
      return {
        profile: null,
        preferences: null,
        organizations: [
          {
            id: activeOrganizationId ?? 'demo-personal-workspace',
            name: 'Personal workspace',
            logo_url: null,
            timezone: 'Asia/Taipei',
            kind: 'personal',
            membership_role: 'owner',
            is_classroom_educator: false,
            revision: 1,
          },
        ],
      };
    }
    return loadMeContext(environment.apiClient);
  }, [activeOrganizationId, environment]);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const context = await readContext();
      setOrganizations(projectTeacherWorkspaces(context.organizations));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [readContext]);

  useEffect(() => {
    if (role !== 'teacher') return;
    void refresh();
  }, [refresh, role]);

  // A SET-F04 organization rename has no shared TanStack Query cache with this component's
  // own local-state fetch, so it notifies through this registry instead of an invalidation.
  useEffect(() => {
    if (role !== 'teacher') return;
    return registerWorkspaceIdentityRefresh(() => void refresh());
  }, [refresh, role]);

  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    if (!menu) return;
    const focusable = () =>
      Array.from(menu.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    queueMicrotask(() => focusable()[0]?.focus());
    const close = () => {
      setOpen(false);
      queueMicrotask(() => triggerRef.current?.focus());
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      close();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (status !== 'denied') return;
    queueMicrotask(() => retryRef.current?.focus());
  }, [status]);

  const currentOrganization = useMemo(
    () =>
      organizations.find((organization) => organization.id === activeOrganizationId) ??
      organizations.find((organization) => organization.kind === 'personal') ??
      null,
    [activeOrganizationId, organizations],
  );

  const selectOrganization = useCallback(
    async (organizationId: string) => {
      setStatus('loading');
      try {
        const context = await readContext();
        const selected = context.organizations.find(
          (organization) =>
            organization.id === organizationId && isTeacherOrganization(organization),
        );
        if (!selected) {
          setOpen(false);
          setStatus('denied');
          return;
        }
        setOrganizations(projectTeacherWorkspaces(context.organizations));
        setOpen(false);
        setStatus('ready');
        navigate(
          selected.kind === 'personal'
            ? '/teacher/workspace'
            : `/teacher/workspace?organizationId=${encodeURIComponent(selected.id)}`,
          selected.kind === 'personal' ? { state: { workspaceSelection: 'personal' } } : undefined,
        );
      } catch {
        setOpen(false);
        setStatus('error');
      }
    },
    [navigate, readContext],
  );

  if (role !== 'teacher') return null;

  if (status === 'loading') {
    return (
      <div
        aria-label="正在載入工作區"
        className={`iam-f12-workspace-switcher iam-f12-workspace-switcher--${surface}`}
        role="status"
      >
        <VispoLoading className="iam-f12-workspace-switcher__loading" label="正在載入工作區…" />
      </div>
    );
  }

  if (status === 'error' || status === 'denied') {
    const denied = status === 'denied';
    return (
      <div
        className={`iam-f12-workspace-switcher iam-f12-workspace-switcher--${surface} iam-f12-workspace-switcher__status`}
        aria-live={denied ? 'assertive' : 'polite'}
        role={denied ? 'alert' : 'status'}
      >
        <span>{denied ? '你目前沒有權限進入這個工作區' : '目前無法載入工作區內容'}</span>
        <button ref={retryRef} type="button" onClick={() => void refresh()}>
          重試
        </button>
      </div>
    );
  }

  const current = currentOrganization ?? organizations[0] ?? null;
  if (!current) {
    return (
      <div
        className={`iam-f12-workspace-switcher iam-f12-workspace-switcher--${surface} iam-f12-workspace-switcher__status`}
        role="status"
      >
        <span>目前沒有可用的老師工作區</span>
        <button type="button" onClick={() => void refresh()}>
          重試
        </button>
      </div>
    );
  }

  const currentName = current.kind === 'personal' ? '個人工作區' : current.name;
  return (
    <div
      className={`iam-f12-workspace-switcher iam-f12-workspace-switcher--${surface}`}
      ref={rootRef}
    >
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`切換工作區：${currentName}`}
        className="iam-f12-workspace-switcher__trigger"
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
      >
        <WorkspaceIdentity organization={current} />
      </button>
      {open ? (
        <div
          aria-label="選擇工作區"
          className="iam-f12-workspace-switcher__menu"
          ref={menuRef}
          role="menu"
        >
          {organizations.map((organization) => {
            const name = organization.kind === 'personal' ? '個人工作區' : organization.name;
            return (
              <button
                aria-current={organization.id === current.id ? 'true' : undefined}
                aria-label={`切換至 ${name}`}
                className="iam-f12-workspace-switcher__choice"
                key={organization.id}
                role="menuitem"
                type="button"
                onClick={() => void selectOrganization(organization.id)}
              >
                <WorkspaceIdentity organization={organization} />
                <span>{name}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function DesktopWorkspaceSwitcher() {
  return <WorkspaceSwitcher surface="desktop" />;
}

export function MobileWorkspaceSwitcher() {
  return <WorkspaceSwitcher surface="mobile" />;
}
